import React, { useEffect, useState } from "react";
import {
  Button,
  CircularProgress,
  IconButton,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  TableContainer,
  Paper,
  TextField,
  InputAdornment,
  Autocomplete,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Snackbar,
  Alert,
  Chip,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import SearchIcon from "@mui/icons-material/Search";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import GroupIcon from "@mui/icons-material/Group";
import { useNavigate } from "react-router-dom";
import axios from "axios";

const BRAND = "#069d6e";
const emailRegex = /^[a-zA-Z0-9_.+-]+@[a-zA-Z0-9-]+\.[a-zA-Z0-9-.]+$/;

const apiBase = () => {
  const baseUrl = process.env.REACT_APP_SERVER_URL || "";
  return baseUrl.endsWith("/") ? baseUrl.slice(0, -1) : baseUrl;
};

export default function Groups() {
  const navigate = useNavigate();
  const [groups, setGroups] = useState([]);
  const [waitlistEmails, setWaitlistEmails] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [toast, setToast] = useState(null);

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [formName, setFormName] = useState("");
  const [formEmails, setFormEmails] = useState([]);
  const [saving, setSaving] = useState(false);

  const loadWaitlist = async () => {
    try {
      const { data } = await axios.get(`${apiBase()}/contacts`);
      const emails = [
        ...new Set(
          (data?.contacts || [])
            .map((c) => c.email)
            .filter((e) => e && e.trim() !== "")
        ),
      ];
      setWaitlistEmails(emails);
    } catch (e) {
      console.error("Waitlist load error:", e);
    }
  };

  const loadGroups = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${apiBase()}/groups`);
      setGroups(data?.groups || []);
    } catch (e) {
      console.error("Groups load error:", e);
      setGroups([]);
    }
    setLoading(false);
  };

  useEffect(() => {
    loadWaitlist();
    loadGroups();
  }, []);

  const openCreate = () => {
    setEditing(null);
    setFormName("");
    setFormEmails([]);
    setDialogOpen(true);
  };

  const openEdit = async (group) => {
    setEditing(group);
    setFormName(group.name || "");
    setFormEmails([]);
    setDialogOpen(true);
    try {
      const { data } = await axios.get(`${apiBase()}/groups/${group.id}`);
      setFormEmails((data?.group?.members || []).map((m) => m.email));
    } catch (e) {
      console.error("Group members load error:", e);
      setToast({ type: "error", msg: "Failed to load group members." });
    }
  };

  const closeDialog = () => {
    if (saving) return;
    setDialogOpen(false);
  };

  const saveGroup = async () => {
    if (!formName.trim()) {
      setToast({ type: "error", msg: "Please enter a group name." });
      return;
    }
    const invalid = formEmails.filter((e) => !emailRegex.test(e));
    if (invalid.length > 0) {
      setToast({ type: "error", msg: `Invalid email(s): ${invalid.join(", ")}` });
      return;
    }

    const waitlistSet = new Set(waitlistEmails);
    const members = formEmails.map((email) => ({
      email,
      source: waitlistSet.has(email) ? "waitlist" : "custom",
    }));

    setSaving(true);
    try {
      if (editing) {
        await axios.put(`${apiBase()}/groups/${editing.id}`, {
          name: formName.trim(),
          members,
        });
        setToast({ type: "success", msg: "Group updated." });
      } else {
        await axios.post(`${apiBase()}/groups`, {
          name: formName.trim(),
          members,
        });
        setToast({ type: "success", msg: "Group created." });
      }
      setDialogOpen(false);
      await loadGroups();
    } catch (error) {
      setToast({
        type: "error",
        msg: error?.response?.data?.message || "Failed to save group.",
      });
    } finally {
      setSaving(false);
    }
  };

  const deleteGroup = async (group) => {
    const ok = window.confirm(`Delete the group "${group.name}"?`);
    if (!ok) return;
    try {
      await axios.delete(`${apiBase()}/groups/${group.id}`);
      setToast({ type: "success", msg: "Group deleted." });
      await loadGroups();
    } catch (error) {
      setToast({
        type: "error",
        msg: error?.response?.data?.message || "Failed to delete group.",
      });
    }
  };

  const formatDate = (value) => {
    if (!value) return "-";
    try {
      return new Date(value).toLocaleDateString();
    } catch {
      return value;
    }
  };

  const filteredGroups = groups.filter((g) =>
    g.name.toLowerCase().includes(search.trim().toLowerCase())
  );

  return (
    <div className="min-h-screen">
      <Snackbar
        open={!!toast}
        autoHideDuration={5000}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
        onClose={() => setToast(null)}
      >
        {toast ? (
          <Alert
            severity={toast.type}
            variant="filled"
            onClose={() => setToast(null)}
            sx={{ width: "100%" }}
          >
            {toast.msg}
          </Alert>
        ) : (
          <span />
        )}
      </Snackbar>

      <div className="p-10 max-md:p-5 flex justify-between items-center">
        <div className="flex items-center gap-3">
          <IconButton onClick={() => navigate("/")}>
            <ArrowBackIcon />
          </IconButton>
          <div className="text-black font-semibold text-lg">Groups</div>
        </div>
        <Button
          onClick={openCreate}
          sx={{
            backgroundColor: BRAND,
            color: "white",
            "&:hover": { backgroundColor: BRAND, color: "white" },
          }}
        >
          Create Group
        </Button>
      </div>

      <div className="px-10 max-md:px-5 pb-10">
        <div className="flex gap-3 mb-4 flex-wrap">
          <TextField
            size="small"
            placeholder="Search groups"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            sx={{ minWidth: 260 }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" />
                </InputAdornment>
              ),
            }}
          />
        </div>

        {loading ? (
          <div className="flex justify-center mt-10">
            <CircularProgress style={{ color: BRAND }} />
          </div>
        ) : filteredGroups.length === 0 ? (
          <div className="flex justify-center text-black mt-10">
            No groups yet. Create one to get started.
          </div>
        ) : (
          <TableContainer component={Paper} sx={{ borderRadius: 2 }}>
            <Table>
              <TableHead>
                <TableRow sx={{ backgroundColor: "#f5f7f9" }}>
                  <TableCell sx={{ fontWeight: 700 }}>Group</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Emails</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Created</TableCell>
                  <TableCell sx={{ fontWeight: 700 }} align="right">
                    Actions
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredGroups.map((g) => (
                  <TableRow key={g.id} hover>
                    <TableCell>
                      <Chip
                        icon={<GroupIcon style={{ color: BRAND }} />}
                        label={g.name}
                        size="small"
                        className="capitalize"
                        sx={{ backgroundColor: "#e6f7f0", color: BRAND }}
                      />
                    </TableCell>
                    <TableCell>{g.member_count} email(s)</TableCell>
                    <TableCell>{formatDate(g.created_at)}</TableCell>
                    <TableCell align="right">
                      <IconButton size="small" onClick={() => openEdit(g)}>
                        <EditIcon fontSize="small" sx={{ color: BRAND }} />
                      </IconButton>
                      <IconButton size="small" onClick={() => deleteGroup(g)}>
                        <DeleteIcon fontSize="small" sx={{ color: "#d32f2f" }} />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </div>

      <Dialog open={dialogOpen} onClose={closeDialog} maxWidth="sm" fullWidth>
        <DialogTitle>{editing ? "Edit Group" : "Create Group"}</DialogTitle>
        <DialogContent dividers>
          <TextField
            fullWidth
            label="Group name"
            value={formName}
            onChange={(e) => setFormName(e.target.value)}
            sx={{ mb: 3, mt: 1 }}
            placeholder="e.g. VIP investors"
          />
          <Autocomplete
            multiple
            freeSolo
            options={waitlistEmails}
            value={formEmails}
            onChange={(_e, val) => setFormEmails([...new Set(val)])}
            filterSelectedOptions
            renderTags={(value, getTagProps) =>
              value.map((option, index) => (
                <Chip
                  label={option}
                  {...getTagProps({ index })}
                  color={emailRegex.test(option) ? "default" : "error"}
                />
              ))
            }
            renderInput={(params) => (
              <TextField
                {...params}
                label="Emails"
                placeholder="Pick from the list or type a custom email + Enter"
                helperText="Choose existing waitlist emails or type any custom email."
              />
            )}
          />
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={closeDialog} disabled={saving} sx={{ color: "#666" }}>
            Cancel
          </Button>
          <Button
            onClick={saveGroup}
            disabled={saving}
            variant="contained"
            sx={{
              backgroundColor: BRAND,
              "&:hover": { backgroundColor: BRAND },
            }}
          >
            {saving ? (
              <CircularProgress size={20} sx={{ color: "#fff" }} />
            ) : editing ? (
              "Save"
            ) : (
              "Create"
            )}
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
}
